/** Browser-model shell (tabs, per-tab history, split panes, tab groups) persisted per workspace. */
import {
  activateTab,
  addToGroup,
  closePane,
  closeTab,
  closeSplitSide,
  closeTabs,
  createGroup,
  createShell,
  duplicateTab,
  focusPane,
  goBack,
  goForward,
  migrateState,
  moveGroup,
  moveTab,
  moveToNewTab,
  moveUnit,
  newTab,
  normalizeUrl,
  openOrFocus,
  openTab,
  otherTabs,
  paneOfTab,
  panes,
  pinTab,
  pinUnit,
  removeFromGroup,
  renameGroup,
  reopenClosed,
  replaceUrl,
  selectTab,
  separateSplit,
  setGroupCollapsed,
  setGroupColor,
  setMaxPanes,
  setRatio,
  splitFocused,
  splitMembers,
  splitPane,
  swapPanes,
  swapSplit,
  tabsToTheRight,
  tabUnits,
  tidyTabs,
  ungroup,
  validate,
  type GroupColor,
  type OpenTarget,
  type ShellState,
  type SplitDirection,
  type SplitSide,
  type StoredShellState,
} from "../logic/shell.ts";
import type { IdGen, KeyValueStorage } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";

const KEY_PREFIX = "bundle.shell.v1.";

function activeUrl(s: ShellState): string | undefined {
  return s.tabs.find((t) => t.id === s.activeTabId)?.url;
}

export type ShellOptions = {
  /**
   * Split groups and tab groups (web: featureFlags.tabs). On: a split view is one strip entry that stays together when
   * another tab is selected, new tabs open on their own, and closes go to the ⌘⇧T stack. Off: the original behaviour
   * (one window-wide split; selecting a tab shows it in the focused pane).
   */
  tabs?: boolean;
  /**
   * The signed-in person. Saved tabs carry it, and tabs saved by someone else (another account signed in on this
   * device) are not restored: that person starts with a fresh shell instead of seeing the previous one's tabs.
   */
  owner?: string;
};

export class ShellViewModel extends ViewModel<ShellState> {
  private readonly storage: KeyValueStorage;
  private readonly ids: IdGen;
  private readonly key: string;
  private readonly groupsMode: boolean;
  private readonly owner: string | undefined;
  private readonly reselects = new Map<string, number>();
  private readonly reselectListeners = new Set<() => void>();

  constructor(storage: KeyValueStorage, ids: IdGen, wsKey: string, homeUrl: string, maxPanes: number, options: ShellOptions = {}) {
    const key = `${KEY_PREFIX}${wsKey}`;
    const tabs = options.tabs === true;
    const initial = ShellViewModel.restore(storage, key, homeUrl, maxPanes, options.owner) ?? createShell(homeUrl, ids.next(), ids.next(), maxPanes);
    super(tabs ? tidyTabs(initial) : initial);
    this.storage = storage;
    this.ids = ids;
    this.key = key;
    this.groupsMode = tabs;
    this.owner = options.owner;
  }

  /**
   * Persisted state from any version (older builds wrote no groups, background splits, closed tabs or owner). Another
   * person's tabs are not restored; state saved without an owner (older builds) is.
   */
  private static restore(storage: KeyValueStorage, key: string, homeUrl: string, maxPanes: number, owner: string | undefined): ShellState | undefined {
    const raw = storage.get(key);
    if (raw === undefined) return undefined;
    try {
      const stored = JSON.parse(raw) as StoredShellState & { owner?: unknown };
      if (owner !== undefined && stored.owner !== undefined && stored.owner !== owner) return undefined;
      if (!Array.isArray(stored.tabs) || typeof stored.layout !== "object" || stored.layout === null) return undefined;
      const s = migrateState(stored);
      if (validate(s).length > 0) return undefined;
      return setMaxPanes({ ...s, homeUrl }, maxPanes);
    } catch {
      return undefined;
    }
  }

  private apply(next: ShellState): void {
    const s = this.groupsMode ? tidyTabs(next) : next;
    if (s === this.state) return;
    this.setState(s);
    this.storage.set(this.key, JSON.stringify(this.owner === undefined ? s : { ...s, owner: this.owner }));
  }

  /**
   * Opens a URL in the focused tab, a new tab, or a split. A plain ("current") open of a view already open in this
   * window switches to it instead (`resolveOpen`): its tab / pane is focused, and nothing changes when it is the active
   * one. Unknown in-app URLs are kept as-is.
   */
  open(url: string, target: OpenTarget = "current"): void {
    const normalized = normalizeUrl(url);
    const next = openOrFocus(this.state, normalized, target, this.ids.next(), this.groupsMode);
    if (target === "current" && next === this.state && activeUrl(this.state) === normalized) this.reselect(this.state.activeTabId);
    this.apply(next);
  }

  /**
   * How many times the view in this tab was opened again while already shown (its sidebar row clicked again): the
   * state doesn't change then, so screens watch this instead (a conversation jumps to its newest message).
   */
  reselectCount(tabId: string): number {
    return this.reselects.get(tabId) ?? 0;
  }

  /** Called after `reselectCount` changes for any tab. */
  readonly subscribeReselect = (listener: () => void): (() => void) => {
    this.reselectListeners.add(listener);
    return () => {
      this.reselectListeners.delete(listener);
    };
  };

  private reselect(tabId: string): void {
    this.reselects.set(tabId, this.reselectCount(tabId) + 1);
    for (const l of Array.from(this.reselectListeners)) l();
  }
  newTab(): void {
    if (this.groupsMode) this.apply(openTab(this.state, this.state.homeUrl, this.ids.next()));
    else this.apply(newTab(this.state, this.ids.next()));
  }
  closeTab(tabId: string = this.state.activeTabId): void {
    if (this.groupsMode) this.closeTabs([tabId]);
    else this.apply(closeTab(this.state, tabId));
  }
  activateTab(tabId: string): void {
    if (this.groupsMode) this.apply(selectTab(this.state, tabId, this.ids.next()));
    else this.apply(activateTab(this.state, tabId));
  }
  /**
   * ⌘1–⌘8 = nth tab, ⌘9 = last tab (browser convention). With tab groups, a split view counts once and tabs in
   * collapsed groups are skipped.
   */
  activateIndex(n: number): void {
    if (this.groupsMode) {
      const collapsed = new Set(this.state.groups.filter((g) => g.collapsed).map((g) => g.id));
      const units = tabUnits(this.state).filter((u) => u.shown || u.groupId === undefined || !collapsed.has(u.groupId));
      const u = n >= 9 ? units[units.length - 1] : units[n - 1];
      if (u !== undefined) this.activateTab(u.tabIds[0]!);
      return;
    }
    const tabs = this.state.tabs;
    const t = n >= 9 ? tabs[tabs.length - 1] : tabs[n - 1];
    if (t !== undefined) this.activateTab(t.id);
  }
  back(): void {
    this.apply(goBack(this.state));
  }
  forward(): void {
    this.apply(goForward(this.state));
  }
  split(direction: SplitDirection = "row"): void {
    this.apply(splitFocused(this.state, direction, this.ids.next()));
  }
  closePane(paneId: string): void {
    this.apply(closePane(this.state, paneId));
  }
  focusPane(paneId: string): void {
    this.apply(focusPane(this.state, paneId));
  }
  setRatio(path: string, ratio: number): void {
    this.apply(setRatio(this.state, path, ratio));
  }
  /** Pins / unpins a tab (with tab groups: its whole split view). */
  pin(tabId: string, pinned: boolean): void {
    this.apply(this.groupsMode ? pinUnit(this.state, tabId, pinned) : pinTab(this.state, tabId, pinned));
  }
  moveTab(tabId: string, toIndex: number): void {
    this.apply(moveTab(this.state, tabId, toIndex));
  }
  setMaxPanes(n: number): void {
    this.apply(setMaxPanes(this.state, n));
  }
  /** Updates the focused tab's URL in place (no history entry), e.g. while typing a search query. */
  replace(url: string): void {
    this.apply(replaceUrl(this.state, this.state.activeTabId, normalizeUrl(url)));
  }

  // ---- Tab management, split groups and tab groups (ShellOptions.tabs) ----

  /** Closes tabs (pushed to the ⌘⇧T stack). */
  closeTabs(tabIds: string[]): void {
    this.apply(closeTabs(this.state, tabIds, this.ids.next()));
  }
  /** "Close others": every unpinned tab outside the given tabs' entries. */
  closeOthers(keepTabIds: string[]): void {
    this.closeTabs(otherTabs(this.state, keepTabIds));
  }
  /** "Close tabs to the right" of a tab's entry (pinned tabs stay). */
  closeToTheRight(tabId: string): void {
    this.closeTabs(tabsToTheRight(this.state, tabId));
  }
  /** ⌘⇧T. */
  reopenClosed(): void {
    this.apply(reopenClosed(this.state, this.ids.next()));
  }
  duplicate(tabId: string): void {
    this.apply(duplicateTab(this.state, tabId, this.ids.next()));
  }
  /** Drag and drop: a tab's entry to `toIndex` (in `tabs` without it), into `groupId` (none when undefined). */
  moveUnit(tabId: string, toIndex: number, groupId: string | undefined): void {
    this.apply(moveUnit(this.state, tabId, toIndex, groupId));
  }
  moveGroup(groupId: string, toIndex: number): void {
    this.apply(moveGroup(this.state, groupId, toIndex));
  }
  /** "Add to new group"; returns the new group's id. */
  createGroup(tabIds: string[], name = ""): string {
    const id = this.ids.next();
    this.apply(createGroup(this.state, tabIds, id, name));
    return id;
  }
  addToGroup(tabIds: string[], groupId: string): void {
    this.apply(addToGroup(this.state, tabIds, groupId));
  }
  removeFromGroup(tabIds: string[]): void {
    this.apply(removeFromGroup(this.state, tabIds));
  }
  ungroup(groupId: string): void {
    this.apply(ungroup(this.state, groupId));
  }
  renameGroup(groupId: string, name: string): void {
    this.apply(renameGroup(this.state, groupId, name));
  }
  setGroupColor(groupId: string, color: GroupColor): void {
    this.apply(setGroupColor(this.state, groupId, color));
  }
  setGroupCollapsed(groupId: string, collapsed: boolean): void {
    this.apply(setGroupCollapsed(this.state, groupId, collapsed, this.ids.next()));
  }
  closeGroup(groupId: string): void {
    const g = this.state.groups.find((x) => x.id === groupId);
    if (g !== undefined) this.closeTabs(g.tabIds);
  }
  /** "Separate tabs". */
  separateSplit(tabId: string): void {
    this.apply(separateSplit(this.state, tabId));
  }
  /** "Swap sides" (⌘⌥\): mirrors the split view holding `tabId`. */
  swapSplit(tabId: string): void {
    this.apply(swapSplit(this.state, tabId));
  }
  /** Drag one segment of a split tab onto another: the two tabs trade places. */
  swapPanes(tabA: string, tabB: string): void {
    this.apply(swapPanes(this.state, tabA, tabB));
  }
  /** "Close both": closes every tab of the split view. */
  closeSplit(tabId: string): void {
    this.closeTabs(splitMembers(this.state, tabId));
  }
  /** The ✕ on one segment of a split tab: closes that tab; its partner stays, focused. */
  closeSplitSide(tabId: string): void {
    this.apply(closeSplitSide(this.state, tabId, this.ids.next()));
  }
  /** "Move to new tab" on one segment of a split tab: that tab leaves the split, shown alone. */
  moveToNewTab(tabId: string): void {
    this.apply(moveToNewTab(this.state, tabId, this.ids.next()));
  }
  /** Drag a tab onto a pane's edge: the tab joins that pane's split view. */
  splitPane(paneId: string, tabId: string, side: SplitSide): void {
    this.apply(splitPane(this.state, paneId, tabId, side, this.ids.next()));
  }
  /**
   * Drag a tab onto another tab (or select several and "Split view"): shows the target's entry and adds the tabs to
   * it, each to the right of the last pane, while the layout has room.
   */
  splitWith(targetTabId: string, tabIds: string[]): void {
    let s = selectTab(this.state, targetTabId, this.ids.next());
    for (const id of tabIds) {
      if (id === targetTabId || paneOfTab(s, id) !== undefined) continue;
      const ps = panes(s.layout);
      const last = ps[ps.length - 1];
      if (last === undefined || ps.length >= s.maxPanes) break;
      s = splitPane(s, last.id, id, "right", this.ids.next());
    }
    this.apply(s);
  }
}
